import { MUITextField } from "@abb/arcadia-ui-react"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { v4 as uuid } from "uuid"

import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useApplications } from "../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../services/api"
import { useApplicationSelectRenderer } from "../../../components/Helpers"
import { TopicForm } from "../../../components/forms/TopicForm"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../models/Authorization"
import { NotificationTopicPost } from "../../../models/NotificationTopic"
import { ControllerFieldRendererProps } from "../../../types/reactHookForm"
import { getErrorMessage } from "../../../utils/getErrorMessage"

const validationSchema = z.object({
  applicationId: z.string().min(1, "Application is required"),
  name: z.string().min(1, "Name is required"),
})

export function TopicCreate() {
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.NM,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const returnURL = `/notifications#topics`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<{ applicationId: string } & NotificationTopicPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      name: "",
    })
  }, [reset])

  const onSubmit = useCallback(
    async (values: { applicationId: string } & NotificationTopicPost) => {
      try {
        const response = await digitalManagerApi.post(
          `/notificationTopics/applications/${values.applicationId}`,
          {
            Id: uuid(),
            ...values,
          }
        )
        if (response.status === StatusCodes.CREATED) {
          toast.success("Notification topic created")
          navigate(returnURL)
        }
      } catch (error) {
        toast.error(getErrorMessage(error, "Cannot create notification topic"))
      }
    },
    [navigate, returnURL]
  )

  const submitForm = useCallback(() => {
    handleSubmit(onSubmit)()
  }, [handleSubmit, onSubmit])

  const handleSelectChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string } & NotificationTopicPost,
          "applicationId"
        >
      ) =>
      (value: string) => {
        field.onChange(value)
      },
    []
  )

  const handleApplicationSelectChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string } & NotificationTopicPost,
          "applicationId"
        >
      ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        handleSelectChange(field)(value)
      },
    [handleSelectChange]
  )

  const handleTextFieldChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string } & NotificationTopicPost,
          "name"
        >
      ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderApplicationIdSelect = useApplicationSelectRenderer({
    applicationsList,
    errors,
    handleApplicationSelectChange,
    isLoadingApplications,
  })

  const renderTextFieldName = useCallback(
    ({
      field,
    }: ControllerFieldRendererProps<
      { applicationId: string } & NotificationTopicPost,
      "name"
    >) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  return (
    <TopicForm
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
      control={control}
      errors={errors}
      renderApplicationSelect={renderApplicationIdSelect}
      renderNameField={renderTextFieldName}
    />
  )
}
